import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from "flarum/common/app";
import Model from "flarum/common/Model";
/**
 * Per-channel notification level. Mirrors Ramon\Chat\ChannelUser.
 */
export let NotificationLevel = /*#__PURE__*/function (NotificationLevel) {
  NotificationLevel[NotificationLevel["Never"] = 0] = "Never";
  NotificationLevel[NotificationLevel["Mentions"] = 1] = "Mentions";
  NotificationLevel[NotificationLevel["Always"] = 2] = "Always";
  return NotificationLevel;
}({});
export default class Channel extends Model {
  constructor() {
    super(...arguments);
    // ── Identity ───────────────────────────────────────────────────────────────
    _defineProperty(this, "type", Model.attribute("type"));
    _defineProperty(this, "name", Model.attribute("name"));
    _defineProperty(this, "slug", Model.attribute("slug"));
    _defineProperty(this, "description", Model.attribute("description"));
    _defineProperty(this, "emoji", Model.attribute("emoji"));
    _defineProperty(this, "imageUrl", Model.attribute("imageUrl"));
    _defineProperty(this, "status", Model.attribute("status"));
    _defineProperty(this, "tagId", Model.attribute("tagId"));
    /**
     * Server-computed label. Direct channels have no stored name — they are named
     * after the other participants, from the reader's perspective — so this is the
     * only correct thing to render.
     */
    _defineProperty(this, "displayName", Model.attribute("displayName"));
    // ── Configuration ──────────────────────────────────────────────────────────
    /** Invitation-only: absent from Browse, and not joinable by a non-member. */
    _defineProperty(this, "isPrivate", Model.attribute("isPrivate"));
    /** 'all' or 'moderators' — who may post here. */
    _defineProperty(this, "postPermission", Model.attribute("postPermission"));
    _defineProperty(this, "threadingEnabled", Model.attribute("threadingEnabled"));
    /** Minimum gap between one person's messages, in seconds. 0 is off. */
    _defineProperty(this, "slowModeSeconds", Model.attribute("slowModeSeconds"));
    /**
     * Seconds this reader must still wait, as the server sees it.
     *
     * Sent per actor rather than derived on the client: a reload has no memory of
     * when you last posted, and a composer that offers a send the server refuses
     * is worse than one that shows the wait.
     */
    _defineProperty(this, "slowModeRemaining", Model.attribute("slowModeRemaining"));
    /**
     * Longest message this channel accepts, or null to follow the forum setting.
     *
     * Null is "inherit", not "unlimited" — see `Channel::maxMessageLength()` on
     * the server, which is the authority. Resolve it with
     * `resolveMaxMessageLength()` rather than reading it raw.
     */
    _defineProperty(this, "maxMessageLength", Model.attribute("maxMessageLength"));
    _defineProperty(this, "autoJoin", Model.attribute("autoJoin"));
    /** Subscribe a user when they reply in the bound category. */
    _defineProperty(this, "autoJoinOnReply", Model.attribute("autoJoinOnReply"));
    /** Announce the bound category's new discussions in the channel. */
    _defineProperty(this, "postDiscussions", Model.attribute("postDiscussions"));
    _defineProperty(this, "allowChannelWideMentions", Model.attribute("allowChannelWideMentions"));
    // ── Counters ───────────────────────────────────────────────────────────────
    _defineProperty(this, "messagesCount", Model.attribute("messagesCount"));
    _defineProperty(this, "userCount", Model.attribute("userCount"));
    _defineProperty(this, "lastMessageId", Model.attribute("lastMessageId"));
    _defineProperty(this, "lastMessageAt", Model.attribute("lastMessageAt", Model.transformDate));
    _defineProperty(this, "createdAt", Model.attribute("createdAt", Model.transformDate));
    _defineProperty(this, "archivedAt", Model.attribute("archivedAt", Model.transformDate));
    _defineProperty(this, "archivedDiscussionId", Model.attribute("archivedDiscussionId"));
    // ── Per-actor membership state ─────────────────────────────────────────────
    _defineProperty(this, "isFollowing", Model.attribute("isFollowing"));
    _defineProperty(this, "isMuted", Model.attribute("isMuted"));
    _defineProperty(this, "notificationLevel", Model.attribute("notificationLevel"));
    _defineProperty(this, "lastReadMessageId", Model.attribute("lastReadMessageId"));
    _defineProperty(this, "unreadCount", Model.attribute("unreadCount"));
    _defineProperty(this, "unreadMentionsCount", Model.attribute("unreadMentionsCount"));
    // ── Capability flags ───────────────────────────────────────────────────────
    // Rendered straight into `disabled`/visibility checks so the client never
    // offers a control the server would reject.
    _defineProperty(this, "canPostMessage", Model.attribute("canPostMessage"));
    _defineProperty(this, "canEdit", Model.attribute("canEdit"));
    _defineProperty(this, "canJoin", Model.attribute("canJoin"));
    /** May join without appearing in the member list — moderators only. */
    _defineProperty(this, "canJoinHidden", Model.attribute("canJoinHidden"));
    /** The actor is in this channel, but invisibly. */
    _defineProperty(this, "isHiddenMember", Model.attribute("isHiddenMember"));
    _defineProperty(this, "canClose", Model.attribute("canClose"));
    _defineProperty(this, "canArchive", Model.attribute("canArchive"));
    _defineProperty(this, "canDelete", Model.attribute("canDelete"));
    _defineProperty(this, "canManageMembers", Model.attribute("canManageMembers"));
    _defineProperty(this, "canMentionChannelWide", Model.attribute("canMentionChannelWide"));
    // ── Relationships ──────────────────────────────────────────────────────────
    _defineProperty(this, "creator", Model.hasOne("creator"));
    _defineProperty(this, "lastMessage", Model.hasOne("lastMessage"));
    _defineProperty(this, "participants", Model.hasMany("participants"));
    /**
     * The other side of a conversation, default-included on the channel list.
     *
     * `participants` is only populated once something asks for it explicitly — the
     * members tab — so the sidebar cannot read its avatars from there. Use
     * `others()` rather than either relation directly.
     */
    _defineProperty(this, "directParticipants", Model.hasMany("directParticipants"));
  }
  // ── Derived helpers ────────────────────────────────────────────────────────

  isDirect() {
    return this.type() === "direct";
  }
  isCategory() {
    return this.type() === "category";
  }
  isOpen() {
    return this.status() === "open";
  }
  isClosed() {
    return this.status() === "closed";
  }
  isArchived() {
    return this.status() === "archived";
  }

  /**
   * Whether a badge should be shown. Muted channels stay listed but never
   * accrue visible pressure.
   */
  hasUnread() {
    var _this$unreadCount;
    return !this.isMuted() && ((_this$unreadCount = this.unreadCount()) != null ? _this$unreadCount : 0) > 0;
  }
  hasUnreadMentions() {
    var _this$unreadMentionsC;
    return ((_this$unreadMentionsC = this.unreadMentionsCount()) != null ? _this$unreadMentionsC : 0) > 0;
  }

  /**
   * Everyone in the channel except the reader, for the avatars a direct channel
   * shows in place of an icon.
   *
   * Reads the default-included `directParticipants` first and falls back to
   * `participants`, which is present once the members tab has been opened. Both
   * return `false` from `hasMany` when the relationship was never included, so
   * neither can be trusted on its own.
   */
  others() {
    var _app$session$user;
    const loaded = this.directParticipants() || this.participants() || [];
    const actorId = (_app$session$user = app.session.user) == null ? void 0 : _app$session$user.id();
    return loaded.filter(user => Boolean(user) && user.id() !== actorId);
  }
  apiEndpoint() {
    return "/chat-channels" + (this.exists ? "/" + this.id() : "");
  }
}
flarum.reg.add('ramon-chat', 'common/models/Channel', Channel);